import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Slider } from '@mui/material'
import { styled } from '@mui/material/styles'
import floor from 'lodash/floor'
import debounce from 'lodash/debounce'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { trackEvent } from '../../analytics/analytics'
import { TRACK_EVENTS } from '../../../constants'

const CustomSlider = styled(Slider)(props => ({
  height: 4,
  '&>.MuiSlider-rail': {
    backgroundImage: `url(${props.bg})`,
    backgroundSize: 'contain',
    opacity: 1
  },
  '&>.MuiSlider-thumb': {
    '&[data-index="0"]': {
      color: `${props.lefthandlecolor} !important`
    },
    '&[data-index="1"]': {
      color: `${props.righthandlecolor} !important`
    }
  }
}))

const SliderWrapper = styled('div')`
  padding: 12px 20px 32px 25px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const InputWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -8px;
  margin-bottom: 10px;
`

const Input = styled('input')`
  font-size: 12px;
  color: white;
  text-align: center;
  background-color: transparent;
  border: none;
  outline: none;
  text-align: center;
  width: 50px;
  padding: 3px;
  /* Underlined like the layer selector above it, mint on hover and while typing. */
  border: 1px solid ${themeColors.transparent};
  border-bottom-color: ${themeColors.mintBorder};
  border-radius: 0;
  &:hover,
  &:focus {
    border-bottom-color: ${themeColors.mintColor};
  }
`

const SliderLabelWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SliderLabel = styled('p')`
  margin-top: -45px;
  margin-bottom: 0;
  font-size: 12px;
  font-weight: 300;
  color: ${themeColors.whiteTransparent08};
  width: 44px;
  text-align: center;
  background-color: transparent;
  border: none;
`

const ColoredRangedSlider = ({ min, max, value, onChange, scale, bg }) => {
  // Determine step size based on range
  const step = max - min > 1 ? 1 : 0.01

  // State for slider values
  const [sliderValues, setSliderValues] = useState([value?.[0] ?? min, value?.[1] ?? max])

  // Separate state for input fields (for immediate feedback while typing)
  const [minInputValue, setMinInputValue] = useState(sliderValues[0].toString())
  const [maxInputValue, setMaxInputValue] = useState(sliderValues[1].toString())

  // Update slider values when props change
  useEffect(() => {
    if (value && Array.isArray(value) && value.length === 2) {
      const formattedValues = [floor(Number(value[0]), step >= 1 ? 0 : 2), floor(Number(value[1]), step >= 1 ? 0 : 2)]
      setSliderValues(formattedValues)
      setMinInputValue(formattedValues[0].toString())
      setMaxInputValue(formattedValues[1].toString())
    }
  }, [value, step])

  // Handler for slider changes
  const handleSliderChange = useCallback((_, newValues) => {
    setSliderValues(newValues)
    setMinInputValue(newValues[0].toString())
    setMaxInputValue(newValues[1].toString())
  }, [])

  // Handler for slider change completed
  const handleSliderChangeCommitted = useCallback(
    (_, newValues) => {
      onChange(newValues)
      trackEvent(TRACK_EVENTS.GM_RANGE_SLIDER_CHANGE_USING_SLIDER, {
        min: newValues[0],
        max: newValues[1],
        previousMin: sliderValues[0],
        previousMax: sliderValues[1]
      })
    },
    [onChange, sliderValues]
  )

  // Function to update slider from min input - not debounced yet
  const updateSliderFromMin = useCallback(
    inputValue => {
      if (inputValue !== '' && !isNaN(inputValue)) {
        let newValue = Number(inputValue)

        // Enforce min constraint
        if (newValue < min) newValue = min

        // Enforce max < min constraint
        if (newValue > sliderValues[1]) newValue = sliderValues[1]

        const newValues = [newValue, sliderValues[1]]
        setSliderValues(newValues)

        // Notify parent component
        onChange(newValues)

        trackEvent(TRACK_EVENTS.GM_RANGE_SLIDER_CHANGE_USING_INPUT, {
          min: newValue,
          max: sliderValues[1],
          previousMin: sliderValues[0],
          previousMax: sliderValues[1]
        })
      }
    },
    [min, sliderValues, onChange]
  )

  // Function to update slider from max input - not debounced yet
  const updateSliderFromMax = useCallback(
    inputValue => {
      if (inputValue !== '' && !isNaN(inputValue)) {
        let newValue = Number(inputValue)

        // Enforce max constraint
        if (newValue > max) newValue = max

        // Enforce max > min constraint
        if (newValue < sliderValues[0]) newValue = sliderValues[0]

        const newValues = [sliderValues[0], newValue]
        setSliderValues(newValues)

        // Notify parent component
        onChange(newValues)

        trackEvent(TRACK_EVENTS.GM_RANGE_SLIDER_CHANGE_USING_INPUT, {
          min: sliderValues[0],
          max: newValue,
          previousMin: sliderValues[0],
          previousMax: sliderValues[1]
        })
      }
    },
    [max, sliderValues, onChange]
  )

  // Create debounced versions of the update functions using useMemo
  const debouncedUpdateMin = useMemo(() => debounce(updateSliderFromMin, 500), [updateSliderFromMin])

  const debouncedUpdateMax = useMemo(() => debounce(updateSliderFromMax, 500), [updateSliderFromMax])

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateMin.cancel()
      debouncedUpdateMax.cancel()
    }
  }, [debouncedUpdateMin, debouncedUpdateMax])

  // Handler for min input field change
  const handleMinInputChange = useCallback(
    e => {
      const inputValue = e.target.value
      // Only update the input state immediately
      setMinInputValue(inputValue)
      // Debounce the slider update to allow for comfortable typing
      debouncedUpdateMin(inputValue)
    },
    [debouncedUpdateMin]
  )

  // Handler for max input field change
  const handleMaxInputChange = useCallback(
    e => {
      const inputValue = e.target.value
      // Only update the input state immediately
      setMaxInputValue(inputValue)
      // Debounce the slider update to allow for comfortable typing
      debouncedUpdateMax(inputValue)
    },
    [debouncedUpdateMax]
  )

  // Handler for input blur - ensures valid values
  const handleInputBlur = useCallback(
    e => {
      const isMinInput = e.target.name === 'minInput'
      const value = isMinInput ? minInputValue : maxInputValue

      if (value === '' || isNaN(value)) {
        // Reset to current slider value if invalid
        if (isMinInput) {
          setMinInputValue(sliderValues[0].toString())
        } else {
          setMaxInputValue(sliderValues[1].toString())
        }
      } else {
        // Force immediate update on blur
        if (isMinInput) {
          debouncedUpdateMin.flush()
        } else {
          debouncedUpdateMax.flush()
        }

        // Ensure proper formatting after update
        const numValue = Number(value)
        const formatted = floor(numValue, step >= 1 ? 0 : 2).toString()

        if (isMinInput) {
          setMinInputValue(formatted)
        } else {
          setMaxInputValue(formatted)
        }
      }
    },
    [minInputValue, maxInputValue, sliderValues, step, debouncedUpdateMin, debouncedUpdateMax]
  )

  return (
    <>
      <SliderWrapper>
        <CustomSlider
          lefthandlecolor={scale(sliderValues[0])}
          righthandlecolor={scale(sliderValues[1])}
          bg={bg}
          valueLabelDisplay="off"
          track={false}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderChangeCommitted}
          value={sliderValues}
          min={min}
          max={max}
          step={step}
        />
      </SliderWrapper>
      <SliderLabelWrapper>
        <SliderLabel>min</SliderLabel>
        <SliderLabel>max</SliderLabel>
      </SliderLabelWrapper>
      <InputWrapper>
        <Input name="minInput" type="number" value={minInputValue} onChange={handleMinInputChange} onBlur={handleInputBlur} step={step} min={min} max={max} />
        <Input name="maxInput" type="number" value={maxInputValue} onChange={handleMaxInputChange} onBlur={handleInputBlur} step={step} min={min} max={max} />
      </InputWrapper>
    </>
  )
}

export default ColoredRangedSlider
